// Client-side metadata for the PAD Info module. Keep the enums and the detail sentences in
// sync with Server/src/interfaces/pad-info/pad-info.interface.ts and
// Server/src/services/pad-info/pad-status.ts.

export const PAD_CATEGORIES = [
  {
    value: "NEW",
    label: "New PAD",
    short: "New",
    color: "#2E7D32",
    bg: "#E8F5E9",
    icon: "plus-circle",
    blurb: "A new resident is moving in and has set up a PAD.",
    fields: ["bank", "amount"],
  },
  {
    value: "CHANGE",
    label: "PAD Change",
    short: "Change",
    color: "#1565C0",
    bg: "#E3F2FD",
    icon: "refresh-cw",
    blurb: "An existing resident changed their banking information.",
    fields: ["bank", "amount"],
  },
  {
    value: "RENEWAL",
    label: "PAD Renewal",
    short: "Renewal",
    color: "#00838F",
    bg: "#E0F7FA",
    icon: "repeat",
    blurb: "The existing PAD continues past a lease renewal / extension.",
    fields: ["renewal", "amount"],
  },
  {
    value: "AMOUNT_CHANGE",
    label: "New Rent Amount",
    short: "Amount",
    color: "#6A1B9A",
    bg: "#F3E5F5",
    icon: "dollar-sign",
    blurb: "The amount changed — rent increase, dog/cat fee, parking, an addendum…",
    fields: ["amount"],
  },
  {
    value: "DELAY",
    label: "PAD Delay",
    short: "Delay",
    color: "#EF6C00",
    bg: "#FFF3E0",
    icon: "clock",
    blurb: "Pull on a different day for one month (e.g. “please pull on the 18th”).",
    fields: ["delay"],
  },
  {
    value: "TEMP_STOP",
    label: "Temporary Stop",
    short: "Temp stop",
    color: "#8D6E63",
    bg: "#EFEBE9",
    icon: "pause-circle",
    blurb: "The resident is temporarily paying another way (e-transfer, cheque…).",
    fields: ["temp_stop"],
  },
  {
    value: "STOP",
    label: "PAD Stop",
    short: "Stop",
    color: "#C62828",
    bg: "#FFEBEE",
    icon: "x-circle",
    blurb: "The resident is moving out or the PAD is cancelled.",
    fields: ["stop"],
  },
];

export const categoryMeta = (value) => PAD_CATEGORIES.find((c) => c.value === value) || PAD_CATEGORIES[0];

export const ALTERNATE_METHODS = ["E-Transfer", "Cheque", "Cash", "Credit Card", "Other"];

export const STOP_REASONS = [
  { value: "MOVING_OUT", label: "Moving out" },
  { value: "BOUGHT_HOUSE", label: "Bought house" },
  { value: "LEASE_BREAK", label: "Lease break" },
  { value: "NOT_RENEWING", label: "Not renewing" },
  { value: "EVICTION", label: "Eviction" },
  { value: "SWITCH_PAYMENT_METHOD", label: "Switching payment method" },
  { value: "OTHER", label: "Other" },
];
const STOP_REASON_LABEL = Object.fromEntries(STOP_REASONS.map((r) => [r.value, r.label]));

/** Mastersheet fee columns that make up the monthly PAD amount, in display order. */
export const LINE_ITEM_KEYS = [
  { key: "basicRent", label: "Rent" },
  { key: "ssFee", label: "SS Parking" },
  { key: "ugFee", label: "UG Parking" },
  { key: "dogFee", label: "Dog Fee" },
  { key: "catFee", label: "Cat Fee" },
  { key: "otherPetFee", label: "Other Pet Fee" },
  { key: "storageFee", label: "Storage Fee" },
  { key: "telus", label: "Telus" },
  { key: "extraTenantFee", label: "Extra Tenant Fee" },
  { key: "otherFee", label: "Other Fee" },
];

export const PAD_STATE_META = {
  ON_PAD: { label: "On PAD", color: "#0F6E56", bg: "#E1F5EE" },
  STOPPED: { label: "PAD stopped", color: "#A32D2D", bg: "#FCEBEB" },
  TEMP_STOPPED: { label: "Temporarily stopped", color: "#854F0B", bg: "#FAEEDA" },
  NO_PAD: { label: "No PAD info", color: "#A32D2D", bg: "#FCEBEB" },
  VACANT: { label: "Vacant", color: "#5F5E5A", bg: "#EEF0F2" },
};

export const SOURCE_META = {
  DATA: { label: "Data in Rentdigicare", color: "#1B5E20", bg: "#E2F0D9", rowClass: "table-success" },
  DOCUMENT: { label: "Document only", color: "#7A5A00", bg: "#FFF2CC", rowClass: "table-warning" },
  NONE: { label: "Nothing available", color: "#8B1A1A", bg: "#F8D7DA", rowClass: "table-danger" },
};

const num = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return isNaN(n) ? 0 : n;
};
export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
export const sumLineItems = (items) => round2((items || []).reduce((s, li) => s + num(li?.amount), 0));

export const money = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = num(v);
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const plain = (v) => {
  const n = num(v);
  const r = round2(n);
  return Number.isInteger(r) ? String(r) : r.toFixed(2);
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const formatMonthLabel = (month) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(month || ""));
  if (!m) return "";
  return `${MONTHS_SHORT[parseInt(m[2], 10) - 1] || m[2]} ${m[1]}`;
};
export const monthOf = (ymd) => (/^\d{4}-\d{2}/.test(String(ymd || "")) ? String(ymd).slice(0, 7) : "");

const bankBit = (bank) => {
  if (!bank) return "";
  const inst = String(bank.institution || "").trim();
  const last4 = String(bank.account_last4 || "").trim();
  const holder = String(bank.account_holder || "").trim();
  const head = [inst, last4 ? `••${last4}` : ""].filter(Boolean).join(" ");
  if (!head && !holder) return "";
  return holder ? `${head || "Account"} (${holder})` : head;
};

/** Same sentence the server prints in the report — one line describing the entry. */
export function formatEntryDetails(e, opts = {}) {
  if (!e) return "";
  const parts = [];
  switch (e.category) {
    case "NEW": {
      parts.push(`First pull ${e.effective_date || "—"}`);
      const b = bankBit(e.bank);
      if (b) parts.push(b);
      break;
    }
    case "CHANGE": {
      parts.push("Banking changed");
      const b = bankBit(e.bank);
      if (b) parts.push(b);
      if (e.previous_amount != null && e.amount != null && round2(num(e.previous_amount)) !== round2(num(e.amount))) {
        parts.push(`Amount ${plain(e.previous_amount)} → ${plain(e.amount)}`);
      }
      break;
    }
    case "RENEWAL":
      parts.push(e.renewal?.lease_end_date ? `Continues to ${e.renewal.lease_end_date}` : "PAD continues under renewal");
      break;
    case "AMOUNT_CHANGE": {
      const changes = Array.isArray(e.changes) ? e.changes : [];
      if (changes.length) parts.push(changes.map((c) => `${c.label || c.key} ${plain(c.from)} → ${plain(c.to)}`).join(", "));
      if (e.previous_amount != null) parts.push(`Total ${plain(e.previous_amount)} → ${plain(e.amount)}`);
      else if (e.amount != null) parts.push(`New total ${plain(e.amount)}`);
      break;
    }
    case "DELAY":
      parts.push(`Pull on ${e.effective_date || "—"} (${formatMonthLabel(e.delay?.month || monthOf(e.effective_date))})`);
      break;
    case "TEMP_STOP": {
      const method = e.temp_stop?.alternate_method;
      const resume = e.temp_stop?.resume_date;
      parts.push(method ? `${method} meanwhile` : "Paying another way meanwhile");
      parts.push(resume ? `resumes ${resume}` : "until further notice");
      break;
    }
    case "STOP": {
      const reason = e.stop?.reason;
      const text = String(e.stop?.reason_text || "").trim();
      if (reason === "OTHER" && text) parts.push(text);
      else {
        parts.push(reason ? STOP_REASON_LABEL[reason] || reason : "PAD stopped");
        if (text) parts.push(text);
      }
      if (e.stop?.move_out_date) parts.push(`move-out ${e.stop.move_out_date}`);
      if (e.stop?.alternate_method) parts.push(`${e.stop.alternate_method} going forward`);
      if (opts.rerented) parts.push("re-rented");
      break;
    }
    default:
      parts.push(categoryMeta(e.category).label);
  }
  return parts.filter(Boolean).join(" · ");
}

/** "YYYY-MM" for next calendar month — accounting prepares the bank file before the 1st. */
export function nextMonthYm(now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth() + 1; // 1..12
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${String(nm).padStart(2, "0")}`;
}

/** Line items pre-filled from a mastersheet item's fee columns (only non-zero ones). */
export function lineItemsFromItem(item) {
  if (!item) return [{ key: "basicRent", label: "Rent", amount: "" }];
  const rows = LINE_ITEM_KEYS.map(({ key, label }) => ({ key, label, amount: num(item[key]) }))
    .filter((r) => r.amount > 0 || r.key === "basicRent");
  return rows.length ? rows : [{ key: "basicRent", label: "Rent", amount: "" }];
}
